import { db, AppState, API_BASE_URL } from './globals.js';
import { loadFlyersView } from './data.js'; 

const locationToggle = document.getElementById("locationToggle");
const locationMenu = document.getElementById("locationMenu");
const locationShortText = document.getElementById("locationShortText");
const locationFullText = document.getElementById("locationFullText");
const updateLocationBtn = document.getElementById("updateLocationBtn");
const manualLocationBtn = document.getElementById("manualLocationBtn");

// ============================================
// LOCATION & GEOCODING FUNCTIONS (Smart Sync)
// ============================================

const DEFAULT_LOCATION = {
  postal_code: "L9T 1A1",
  city: "Milton",
  mapped_city: "Milton",
  state: "Ontario",
  state_code: "ON",
  country: "Canada",
  latitude: 43.5234,
  longitude: -79.8711
};

export function updateLocationUI(locData) {
  localStorage.setItem("walleto_location", JSON.stringify(locData));
  if (!locData) return;
  
  // 1. Sync ALL location data to global state for the Flyer API
  AppState.selectedCountryName = locData.country || "Canada";
  AppState.mapped_city = locData.mapped_city || locData.mappedCity || locData.city || "Milton";
  AppState.city = AppState.mapped_city;
  
  // 2. Handle both API response formats (snake_case) and Firebase formats (camelCase)
  const displayCity = AppState.mapped_city;
  const displayPostal = locData.postal_code || locData.postalCode || "";
  
  let displayShort = displayPostal || displayCity;
  if (displayShort.length > 22) { 
    displayShort = displayShort.substring(0, 19) + "...";
  }
  
  // 3. Update Navbar
  locationShortText.textContent = displayShort;
  
  // 4. Update Dropdown
  locationFullText.innerHTML = `<strong>${displayCity}</strong><br>${locData.state || ""}, ${locData.country || ""}<br>${displayPostal}`;

  // 5. Update Mobile Menu
  const mobileLocText = document.getElementById("mobileLocationText");
  if (mobileLocText) {
    mobileLocText.textContent = displayShort;
  }

  // 6. Update Flyers heading postal code (if present)
  const flyersPostalEl = document.getElementById("flyersPostalCode");
  if (flyersPostalEl) {
    flyersPostalEl.textContent = displayPostal || "L9T 1A1";
  }
}

export async function fetchUserLocation(isUserInitiated = false) {
  locationShortText.innerHTML = "Locating...";
  locationFullText.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Fetching precise address...';

  const applyFallback = () => {
    console.warn("Applying ultimate fallback location (Milton, ON).");
    updateLocationUI(DEFAULT_LOCATION);
  };

  try {
    // SCENARIO A: Check if user already has location saved in Firestore
    if (AppState.isLoggedIn && AppState.user_uid !== "guest" && db) {
      const userDoc = await db.collection("users").doc(AppState.user_uid).get();
      
      if (userDoc.exists) {
        const userData = userDoc.data();
        if (userData.postalCode && (userData.mappedCity || userData.mapped_city)) {
          console.log("Location loaded from database.");
          updateLocationUI(userData);
          if (isUserInitiated) locationMenu.classList.remove("show");
          return; // Exit early!
        }
      }
    }

    // NEW: SCENARIO A.5: Check local browser cache (for guests or returning users)
    if (!isUserInitiated) {
      const cachedLoc = localStorage.getItem("walleto_location");
      if (cachedLoc) {
        console.log("Location loaded from browser cache.");
        updateLocationUI(JSON.parse(cachedLoc));
        return; // Exit early! Use the cached location.
      }
    }

    // SCENARIO B: No saved data -> Request GPS
    if (!navigator.geolocation) {
      throw new Error("Geolocation not supported by browser.");
    }

    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
    });
    
    const { latitude, longitude } = position.coords;

    // Call Nominatim API just to get the postal code
    const reverseGeoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
    const reverseGeoData = await reverseGeoRes.json();
    const extractedPostalCode = reverseGeoData?.address?.postcode;

    if (!extractedPostalCode) {
      throw new Error("Could not extract postal code from coordinates.");
    }

    // Pass postal code to Render API to get the standardized location payload
    const renderApiRes = await fetch(`${API_BASE_URL}/getLocationFromPostalCode?postal_code=${encodeURIComponent(extractedPostalCode)}`);
    const renderApiData = await renderApiRes.json();

    if (!renderApiRes.ok || renderApiData.detail === "Invalid postal code format") {
      throw new Error("Invalid postal code format returned by API.");
    }

    // Success! Update UI
    updateLocationUI(renderApiData);

    // Save to Firestore (only if logged in)
    if (AppState.isLoggedIn && AppState.user_uid !== "guest") {
      await db.collection("users").doc(AppState.user_uid).set({
        postalCode: renderApiData.postal_code,
        mappedCity: renderApiData.mapped_city,
        city: renderApiData.city,
        state: renderApiData.state,
        country: renderApiData.country,
        latitude: renderApiData.latitude,
        longitude: renderApiData.longitude
      }, { merge: true });
    }

    if (isUserInitiated) {
      loadFlyersView();
    }

    if (isUserInitiated) locationMenu.classList.remove("show");

  } catch (error) {
    // SCENARIO C: Ultimate Fallback (Permission Denied, Indian Postal Code, etc.)
    console.warn("Location Fetch Failed / Denied:", error.message);
    if (isUserInitiated && error.code === 1) { // Geolocation Permission Denied
      alert("Location access is blocked by your browser. Defaulting to Milton, ON.");
    }
    applyFallback();
  }
}

export function setupLocationFeature() {
  if(!locationToggle) return;

  // Shared helper function to process manual postal code entry via API
  const handleManualInput = async (manualEntry, isMobile) => {
    locationShortText.innerHTML = "Locating...";
    locationFullText.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Fetching address...';

    try {
      // 1. Call the Render API with the user's manually entered postal code
      const renderApiRes = await fetch(`${API_BASE_URL}/getLocationFromPostalCode?postal_code=${encodeURIComponent(manualEntry)}`);
      const renderApiData = await renderApiRes.json();

      if (!renderApiRes.ok || renderApiData.detail === "Invalid postal code format") {
        throw new Error("Invalid postal code format.");
      }

      // 2. Update the UI
      updateLocationUI(renderApiData);

      // 3. Save it to the database if logged in
      if (AppState.isLoggedIn && AppState.user_uid !== "guest") {
        await db.collection("users").doc(AppState.user_uid).set({
          postalCode: renderApiData.postal_code,
          mappedCity: renderApiData.mapped_city,
          city: renderApiData.city,
          state: renderApiData.state,
          country: renderApiData.country,
          latitude: renderApiData.latitude,
          longitude: renderApiData.longitude
        }, { merge: true });
      }
      loadFlyersView();
    } catch (error) {
      console.error("Manual Entry Failed:", error);
      alert("Invalid Postal/Zip Code. Defaulting to Milton, ON.");
      updateLocationUI(DEFAULT_LOCATION);
    } finally {
      // Close the respective menus
      if (isMobile) {
        document.getElementById("mobileMenu")?.classList.remove("active");
      } else {
        locationMenu.classList.remove("show");
      }
    }
  };

  // --- CUSTOM MODAL LOGIC ---
  const manualModal = document.getElementById("manualLocationModal");
  const manualInput = document.getElementById("manualLocationInput");
  const manualSubmit = document.getElementById("manualLocationSubmitBtn");
  const manualCancel = document.getElementById("manualLocationCancelBtn");
  const manualClose = document.getElementById("manualLocationCloseBtn");
  const manualOverlay = document.getElementById("manualLocationOverlay");

  let isMobileRequest = false;

  const openManualModal = (isMobile) => {
    isMobileRequest = isMobile;
    if (manualInput) manualInput.value = "";
    if (manualModal) manualModal.classList.add("active");
    setTimeout(() => { if (manualInput) manualInput.focus(); }, 100);
  };

  const closeManualModal = () => {
    if (manualModal) manualModal.classList.remove("active");
  };

  const submitManualLocation = () => {
    if (!manualInput) return;
    const val = manualInput.value.trim();
    if (val) {
      closeManualModal();
      handleManualInput(val, isMobileRequest);
    }
  };

  // Connect custom modal buttons safely
  if (manualSubmit) manualSubmit.addEventListener("click", submitManualLocation);
  if (manualInput) manualInput.addEventListener("keypress", (e) => { if (e.key === "Enter") submitManualLocation(); });
  if (manualCancel) manualCancel.addEventListener("click", closeManualModal);
  if (manualClose) manualClose.addEventListener("click", closeManualModal);
  if (manualOverlay) manualOverlay.addEventListener("click", closeManualModal);

  // --- MENU TRIGGER LOGIC ---
  const mobileLocationBtn = document.getElementById("mobileLocationBtn");
  if (mobileLocationBtn) {
    mobileLocationBtn.addEventListener("click", () => {
      const choice = confirm("Update Location:\n\nClick 'OK' to use your current GPS location.\nClick 'Cancel' to enter your Zip/Postal Code manually.");
      
      if (choice) {
        fetchUserLocation(true);
        document.getElementById("mobileMenu").classList.remove("active");
      } else {
        openManualModal(true); // Triggers custom modal instead of prompt
      }
    });
  }

  locationToggle.addEventListener("click", function (e) {
    e.stopPropagation();
    document.getElementById("dropdownMenu")?.classList.remove("show");
    document.getElementById("countryMenu")?.classList.remove("show");
    locationMenu.classList.toggle("show");
  });

  document.addEventListener("click", function (e) {
    if (!locationToggle.contains(e.target) && !locationMenu.contains(e.target)) {
      locationMenu.classList.remove("show");
    }
  });

  updateLocationBtn.addEventListener("click", () => {
    fetchUserLocation(true);
  });

  manualLocationBtn.addEventListener("click", () => {
    openManualModal(false); // Triggers custom modal instead of prompt
  });

  // Always run the smart location fetch on load
  fetchUserLocation(false);
}

// Trigger initialization when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  setupLocationFeature();
});